import { useState, useEffect } from "react";
import "./Home.css";
import { useCoreDataLoader } from "../../hooks/useCoreDataLoader";
import { useNavigate, useParams } from "react-router-dom";
import { apiCopyForm, apiCreateFormInWorkspace, apiDeleteForm, apiGetForm, apiRenameForm, apiUpdateFormTags } from "../../services/FormService";
import { useSelector } from "react-redux";
import { 
    selectForms,
    selectTags,
    selectUIStates,
    selectUser,
    setEditingForm,
    setEditingQuestionId,
    setForms, 
    setUIStates,
    setViewingFormThemeSettings} from "../../redux/features/appData";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../config";
import { useDispatch } from "react-redux";
import { CopyFormPayload, FormExtended, iconPath, RenameFormPayload, Tag, TagExtended, UpdateFormTagsPayload } from "effno-core";
import { FormDeleteModal } from "./FormDeleteModal";
import { FormRenameModal } from "./FormRenameModal";
import { NewTagModal } from "../tags/NewTagModal";
import { apiCreateTagInWorkspace } from "../../services/TagService";
import { UpdateTagsModal } from "../tags/UpdateTagsModal";
import { apiGetFormResponseCountForForm } from "../../services/ResponseService";
import { ICONS } from "../shared/media";
import HomeMenu from "./HomeMenu";
import { TagCard } from "../shared/TagCard";
import { FormThemeSettings } from "../question-design/QuestionDesign";
import { QUESTION_SETTINGS_TABS } from "../question-settings/QuestionSettings";
import { CircleAlert, CirclePlus, Plus, Tag as TagIcon, Users } from "lucide-react";

const debugMode = false;

export default function Home() {

    const storeUser = useSelector(selectUser);
    const storeForms = useSelector(selectForms)
    const storeTags = useSelector(selectTags);
    const uiStates = useSelector(selectUIStates);
    
    const { reloadAll }  = useCoreDataLoader();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();

    const [deleteFormModalIsOpen, setDeleteFormModalIsOpen] = useState<boolean>(false);
    const [renameFormModalIsOpen, setRenameFormModalIsOpen] = useState<boolean>(false);
    const [selectedFormId, setSelectedFormId] = useState<string | undefined>(undefined);
    const [newTagModalIsOpen, setNewTagModalIsOpen] = useState<boolean>(false);
    const [viewingTagId, setViewingTagId] = useState<string | undefined>(undefined);
    const [updateTagsModalIsOpen, setUpdateTagsModalIsOpen] = useState<boolean>(false);
    const [formResponseCounts, setFormResponseCounts] = useState<{[key: string]: number} | undefined>(undefined);

    useEffect(() => {
        if (!uiStates) {
            return;
        }

        dispatch(setUIStates({
            ...uiStates,
            viewingResponses: false,
            viewingShare: false
        }));
        
    }, [])

    useEffect(() => {
        if (storeForms && !formResponseCounts) {

            (async () => {
                const counts = {} as {[key: string]: number};

                for (let i = 0; i < storeForms.length; i++) {
                    const count = await apiGetFormResponseCountForForm(storeForms[i].id);

                    if (count !== null) {
                        counts[storeForms[i].id] = count;
                    }
                }
                
                setFormResponseCounts(counts);
            })();

        }
    }, [storeForms, storeTags])

    useEffect(() => {
        setViewingTagId(params.tagId);
    }, [params]);

    const generateUniqueFormName = (baseName: string, existingForms: FormExtended[] | undefined): string => {

        if (!existingForms) {
            return baseName;
        }

        let newName = baseName;
        let copyCount = 1;
    
        while (existingForms.some(form => form.name === newName)) {
            newName = `${baseName} Copy ${copyCount}`;
            copyCount++;
        }
    
        return newName;
    };

    const handleNewForm = async () => {
        if (!storeUser?.currentWorkspaceId) {
            toast("No workspace selected", toastFail);
            return;
        }

        // clear out some of the old stuff
        dispatch(setEditingForm(undefined));
        dispatch(setViewingFormThemeSettings({}));
        dispatch(setEditingQuestionId(""));
        dispatch(setUIStates({
            ...uiStates, 
            questionSettingsTab: QUESTION_SETTINGS_TABS.SETTINGS,
            formSettingsOpen: false,
            questionPickerOpen: false,
            designSettingsOpen: false,
            viewingBuild: true,
            viewingDesign: false,
            viewingSettings: false,
            viewingPreview: false,
            viewingComments: false,
            viewingResponses: false,
            viewingShare: false,
            mobileLayout: false,
            commentMode: false,
            editingFormTheme: false,
            viewingComment: false,
        }));

        // find an appropriate name
        const baseName = "My Form";
        const uniqueName = generateUniqueFormName(baseName, storeForms);
        console.log("[FormEditor] uniqueName: ", uniqueName);

        let form = await apiCreateFormInWorkspace(storeUser.currentWorkspaceId);

        if (!form) {
            toast("Failed to create form", toastFail);
            return;
        }

        form.name = uniqueName;

        // if we are viewing a tagId, add it
        if (viewingTagId) {
            const payload : UpdateFormTagsPayload = {
                formId: form.id,
                tagIds: [viewingTagId]
            }

            form = await apiUpdateFormTags(payload);

            if (!form) {
                toast("Failed to assign tag to new form", toastFail);
                return;
            }
        }

        // load everything into redux
        dispatch(setEditingForm(form));

        if (!storeForms) {
            dispatch(setForms([form]));
        } else {
            dispatch(setForms([...storeForms, form]));
        }

        // make sure we save the rename - async 
        const payload = {
            formId: form.id,
            name: form.name
        } as RenameFormPayload;

        const renamedForm = apiRenameForm(payload);
        navigate("/editor");
    }

    const handleNewTag = () => {
        setNewTagModalIsOpen(true);
    }

    const handleEditForm = (form: FormExtended) => {
        dispatch(setEditingForm({...form}));
        dispatch(setViewingFormThemeSettings(
            JSON.parse(form?.formTheme?.settings || "{}") as FormThemeSettings
        ));
        let questionId = "";
        
        if (form.questions.length > 0) {
            const questionOrder = JSON.parse(form.questionOrder || "[]");

            if (questionOrder.length > 0) {
                questionId = questionOrder[0];
            }
        }
        dispatch(setEditingQuestionId(questionId));
        console.log("setting questionId: ", questionId);
        dispatch(setUIStates({
            ...uiStates, 
            mobileLayout: false,
            questionSettingsTab: QUESTION_SETTINGS_TABS.SETTINGS,
            viewingBuild: true,
            viewingDesign: false,
            viewingSettings: false,
            viewingPreview: false,
            viewingComments: false,
            viewingResponses: false,
            viewingShare: false,
            openingDesign: false,
            commentMode: false,
        }));
        navigate("/editor");
    }

    const handleDeleteForm = (formId: string) => {
        setSelectedFormId(formId);
        setDeleteFormModalIsOpen(true);
    }

    const handleRenameForm = (formId: string) => {
        setSelectedFormId(formId);
        setRenameFormModalIsOpen(true);
    }

    const handleUpdateTags = (formId: string) => {
        setSelectedFormId(formId);
        setUpdateTagsModalIsOpen(true);
    }

    const handleCopyForm = async (form: FormExtended) => {

        const payload : CopyFormPayload = {
            formId: form.id,
            name: "Copy of " + form.name,
            includeWorkflow: true,
            createMap: false,
        }

        const formCopy = await apiCopyForm(payload);

        if (!formCopy) {
            toast("Failed to copy form", toastFail);
            return;
        }

        if (!storeForms) {
            dispatch(setForms([formCopy]));
        } else {
            dispatch(setForms([...storeForms, formCopy]));
        }

        toast("Copied Form", toastSuccess);
        return;
    }

    const handleAddTeam = () => {
        navigate("/workspaces?tab=3");
    }

    const deleteForm = async () => {
        if (!selectedFormId || !storeUser){
            return;
        }
        
        const deletedForm = await apiDeleteForm(selectedFormId);

        if (!deletedForm) {
            toast("Failed to delete form", toastFail);
            return;
        }

        setSelectedFormId(undefined);
        reloadAll(storeUser.currentWorkspaceId);
    }

    const renameForm = async (name: string) => {
        if (!selectedFormId || !storeUser){
            return;
        }
        
        const payload = {
            formId: selectedFormId,
            name
        } as RenameFormPayload;

        const renamedForm = await apiRenameForm(payload);

        if (!renamedForm) {
            toast("Failed to rename form", toastFail);
            return;
        }

        setSelectedFormId(undefined);
        reloadAll(storeUser.currentWorkspaceId);
    }

    const createNewTag = async (name: string) => {
        if (!storeUser?.currentWorkspaceId) {
            return;
        }

        console.log("creating new tag: ", name);

        const payload = {
            workspaceId: storeUser.currentWorkspaceId,
            name
        }

        const tag = await apiCreateTagInWorkspace(payload);

        if (!tag) {
            toast("Failed to create tag", toastFail);
            return;
        }

        toast("Added Tag", toastSuccess);
        reloadAll(storeUser.currentWorkspaceId);
    }

    // const viewingForms : [] | undefined = [];

    const viewingForms = storeForms ? storeForms?.filter((form: FormExtended) => {
        if (!viewingTagId || !form.tags) {
            return true;
        }

        let found = false;
        form.tags.forEach((tag: Tag) => {
            if (tag.id === viewingTagId) {
                found = true;
                return;
            }
        })

        return found;
    }) : undefined;

    const theTag = viewingTagId ? storeTags?.find((tag: TagExtended) => tag.id === viewingTagId) : undefined;

    console.log("[Home] tagId: ", viewingTagId);


    return (
        <div id="home">
            <div className="home-header">
                <div 
                    className="page-title"
                    style={{cursor: "pointer"}}
                    onClick={() => navigate("/home")}
                >
                    Home           
                </div>
                <div className="home-header-tag">
                    {theTag && 
                            <TagCard 
                                tag={theTag}
                                allowClick={false}
                            />
                    }
                </div>
                <div className="home-header-actions">
                    {viewingForms && viewingForms.length > 0 &&
                        <div 
                            className="home-header-action"
                            onClick={handleNewTag}
                        >
                            <TagIcon size={16} />
                            {/* <img src={iconPath(ICONS.TAGS, storeUser)} /> */}
                            New Tag
                        </div>
                    }

                    <div 
                        className="home-header-action home-header-action-selected"
                        onClick={handleNewForm}
                    >
                        <Plus size={16} />
                          {/* <img src={iconPath(ICONS.FORMS, storeUser)} /> */}
                        New Form
                    </div>

                </div>
            </div>

            <div className="home-forms">
                {viewingForms && viewingForms.map((form: FormExtended) => {
                    return (
                        <div 
                            className="home-form"
                            onClick={()=>handleEditForm(form)}
                        >
                            <div className="home-form-entry">
                                <div className="home-form-title">
                                    <span>{form.name}&nbsp;{debugMode && <b>{form.id}</b>}</span>
                                    {form.tags && form.tags.length > 0 &&
                                        <div className="home-form-tags">
                                            {form.tags.map((tag: Tag) => {
                                                return (
                                                    <div 
                                                        className="home-form-tag"
                                                        onClick={(e: any) => {
                                                            e.stopPropagation();
                                                            navigate(`/home/${tag.id}`);
                                                        }}
                                                    >
                                                        {tag.name}
                                                    </div>  
                                                )
                                            })}
                                        </div>
                                    }
                                    </div>

                                <div 
                                    className="home-form-response-count"
                                    onClick={(e: any) => {
                                        dispatch(setEditingForm({...form}));
                                        dispatch(setViewingFormThemeSettings(
                                            JSON.parse(form?.formTheme?.settings || "{}") as FormThemeSettings
                                        ));
                                        navigate(`/responses/${form.id}`);
                                        e.stopPropagation();
                                    }}
                                >
                                    {formResponseCounts && 
                                        formResponseCounts[form.id] ? 
                                            formResponseCounts[form.id] : 0
                                    } 
                                    &nbsp;responses
                                </div>

                                <div className="home-form-actions">
                                    <HomeMenu 
                                        formId={form.id}
                                        onUpdateTags={(formId: string)=>{
                                            handleUpdateTags(form.id)
                                        }}
                                        onDelete={(e: any)=>{
                                            handleDeleteForm(form.id)
                                        }}
                                        onRename={(e: any)=>{
                                            handleRenameForm(form.id)
                                        }}
                                        onCopy={(e: any)=>{
                                            handleCopyForm(form)
                                        }}
                                    />
                                </div>

                                
                            </div>   
                            
                        </div>
                    )
                    })
                }

                {storeForms && storeForms.length > 0 && (!viewingForms || viewingForms.length === 0) &&
                    <div className="home-form-zero">
                        No forms with this tag
                    </div>
                }

                {!storeForms || storeForms.length === 0 &&
                    <div className="home-form-zero">
                        <CircleAlert size={32}/>
                        <div className="home-form-zero-text">
                            No forms in this workspace yet
                        </div>
                        <div className="home-form-zero-text">
                            What would you like to do?
                        </div>
                        <div className="home-form-zero-actions">
                            <button
                                className="button-cancel"
                                onClick={handleNewForm}
                            >
                                <div className="home-form-zero-button">
                                    <CirclePlus size={16}/>
                                    Create Form
                                </div>
                            </button> 
                            <button
                                className="button-cancel"
                                onClick={handleAddTeam}
                            >
                                <div className="home-form-zero-button">
                                    <Users size={16}/>
                                    Invite Team
                                </div>
                            </button>

                        </div>
                    </div>
                
                }

            </div>

            {deleteFormModalIsOpen && 
                <FormDeleteModal 
                    isOpen={deleteFormModalIsOpen}
                    onClose={() => {
                        setDeleteFormModalIsOpen(false)
                    }} 
                    onSubmit={() => {
                        setDeleteFormModalIsOpen(false)
                        deleteForm();
                    }}
                />
            }

            {renameFormModalIsOpen && 
                <FormRenameModal 
                    isOpen={renameFormModalIsOpen}
                    onClose={() => {
                        setRenameFormModalIsOpen(false)
                    }} 
                    onSubmit={(name: string) => {
                        setRenameFormModalIsOpen(false)
                        renameForm(name);
                    }}
                    defaultValue={storeForms?.find((form: FormExtended) => form.id === selectedFormId)?.name || ""}
                />
            }

            {newTagModalIsOpen && 
                <NewTagModal 
                    isOpen={newTagModalIsOpen}
                    onClose={() => {
                        setNewTagModalIsOpen(false)
                    }} 
                    onSubmit={(name: string) => {
                        setNewTagModalIsOpen(false)
                        createNewTag(name);
                    }}
                />
            }   

            {updateTagsModalIsOpen && 
                <UpdateTagsModal
                    formId={selectedFormId || ""}
                    isOpen={updateTagsModalIsOpen}
                    onClose={() => {
                        setUpdateTagsModalIsOpen(false)
                    }} 
                    onSubmit={() => {
                        reloadAll(storeUser?.currentWorkspaceId || "");
                        toast("Updated Tags", toastSuccess);
                        setUpdateTagsModalIsOpen(false)
                    }}
                />
            }   
        </div>
        
    )
}